import axios from "axios";
import { downloadFile } from "../../utils/commonUtils";
import { IDownloadLogsRequestData, IDownloadReportStatusResponse, IGetUptimeLogsRequestData, IGetReportsRequestData } from "../../types";
import { BASE_API_URL, getAPIHeaderOptions } from "../../utils/apiUtils";

const DOWNLOAD_FILENAME_IDENTIFIER = "filename=";

const handleDownloadLogVideo = (videoUrl: string, fileName: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(videoUrl.replace("http://", "https://"), { responseType: "blob" })
			.then((response) => {
				if (response.data) {
					downloadFile(new Blob([response.data]), fileName);
					resolve();
					return;
				}

				reject("Failed to download video. Please try again later");
			})
			.catch((error) => {
				reject(error);
			});
	});
};

const handleDownloadLogs = (requestData: IDownloadLogsRequestData): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/analytics/downloadexcel/download_event_logs`, {
				headers: getAPIHeaderOptions(true),
				params: requestData,
				responseType: "blob"
			})
			.then((response) => {
				if (response.data) {
					const responseContent = String(response.headers["content-disposition"]);
					const fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "";

					downloadFile(new Blob([response.data]), fileName);
					resolve();
				} else {
					reject("Something went wrong");
				}
			})
			.catch((error) => reject(error?.response?.data?.detail));
	});
};

const handleDownloadUptimeLogs = (requestData: IGetUptimeLogsRequestData): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/devices/download-uptime-logs`, {
				headers: getAPIHeaderOptions(true),
				responseType: "blob",
				params: {
					start_time: requestData.start_time,
					end_time: requestData.end_time,
					device: requestData.device_id
				}
			})
			.then((response) => {
				if (response.data) {
					const responseContent = response.headers["content-disposition"];
					const fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "";

					downloadFile(new Blob([response.data]), fileName);
					resolve();
				} else {
					reject("Something went wrong");
				}
			})
			.catch((error) => reject(error?.response?.data?.detail ?? "Something went wrong"));
	});
};

const handleDownloadAlerts = (requestData: IDownloadLogsRequestData): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/analytics/downloadexcel/get_download_alerts_log`, {
				headers: getAPIHeaderOptions(true),
				params: requestData,
				responseType: "blob"
			})
			.then((response) => {
				if (response.data) {
					const responseContent = String(response.headers["content-disposition"]);
					const fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "";

					downloadFile(new Blob([response.data]), fileName);
					resolve();
				} else {
					reject("Something went wrong");
				}
			})
			.catch((error) => reject(error?.response?.data?.detail));
	});
};

const handleDownloadViolationLogs = (requestData: IDownloadLogsRequestData): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/analytics/downloadexcel/get_download_alerts_log`, {
				headers: getAPIHeaderOptions(true),
				params: requestData,
				responseType: "blob"
			})
			.then((response) => {
				if (response.data) {
					const responseContent = String(response.headers["content-disposition"]);
					const fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "Violation_Logs.xlsx";

					downloadFile(new Blob([response.data]), fileName);
					resolve();
				} else {
					reject("Something went wrong");
				}
			})
			.catch((error) => reject(error?.response?.data?.detail));
	});
};

const handleDownloadReport = (job_uuid: string, download_url?: string, sseFilename?: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		const reportDownloadURL = download_url
			? download_url
			: `${BASE_API_URL}/reports/${job_uuid}/download`;

		axios
			.get(reportDownloadURL, {
				headers: getAPIHeaderOptions(true),
				responseType: "blob"
			})
			.then((response) => {
				if (!response.data) {
					reject("Something went wrong");
					return;
				}

				const responseContent = String(response.headers["content-disposition"] || "");
				let fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)?.[1] ?? "";

				// Remove surrounding quotes if present.
				fileName = fileName.replace(/(^['"]|['"]$)/g, "").trim();
				if (!fileName && sseFilename) fileName = sseFilename.trim();
				if (!fileName) fileName = "report.xlsx";

				downloadFile(new Blob([response.data]), fileName);
				resolve();
			})
			.catch((error) => reject(error?.response?.data?.detail ?? "Something went wrong"));
	});
};

const getReportJobStatus = (job_uuid: string): Promise<IDownloadReportStatusResponse> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/reports/${job_uuid}/status`, {
				headers: getAPIHeaderOptions(true)
			})
			.then((response) => {
				if (!response.data) {
					reject("Something went wrong");
					return;
				}

				resolve(response.data as IDownloadReportStatusResponse);
			})
			.catch((error) => reject(error?.response?.data?.detail ?? "Something went wrong"));
	});
};

const handleDownloadReports = (requestData: IGetReportsRequestData): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/analytics/reports/download`, {
				headers: getAPIHeaderOptions(true),
				params: requestData,
				responseType: "blob"
			})
			.then((response) => {
				if (response.data) {
					const responseContent = String(response.headers["content-disposition"] || "");
					let fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "reports.csv";

					// Remove quotes if present and trim whitespace
					fileName = fileName.replace(/^["']|["']$/g, "").trim();

					// Remove any trailing underscores or other unwanted characters
					fileName = fileName.replace(/^_+|_+$/g, "");

					downloadFile(new Blob([response.data]), fileName || "reports.csv");
					resolve();
				} else {
					reject("Something went wrong");
				}
			})
			.catch((error) => reject(error?.response?.data?.detail ?? "Something went wrong"));
	});
};

const handleDownloadNicApiUsage = (requestData: { start_date?: string; end_date?: string }): Promise<void> => {
	return new Promise((resolve, reject) => {
		axios
			.get(`${BASE_API_URL}/analytics/reports/nic-api-usage/download`, {
				// Backend middleware only accepts "*/*" or "application/json" here, not the xlsx MIME type.
				headers: getAPIHeaderOptions(true),
				params: requestData,
				responseType: "blob"
			})
			.then((response) => {
				if (!response.data) {
					reject("Something went wrong");
					return;
				}

				const responseContent = String(response.headers["content-disposition"] || "");
				let fileName = responseContent.split(DOWNLOAD_FILENAME_IDENTIFIER)[1] ?? "";
				fileName = fileName.replace(/(^['"]|['"]$)/g, "").trim();

				downloadFile(new Blob([response.data]), fileName || "nic_api_usage.xlsx");
				resolve();
			})
			.catch(async (error) => {
				// Error bodies arrive as a Blob because of responseType, so parse them for the detail message.
				const errorData = error?.response?.data;
				if (errorData instanceof Blob) {
					try {
						const parsed = JSON.parse(await errorData.text());
						reject(parsed?.detail ?? parsed?.error ?? "Something went wrong");
						return;
					} catch {
						// Fall through to the generic message.
					}
				}
				reject(errorData?.detail ?? "Something went wrong");
			});
	});
};

const downloadServices = {
	handleDownloadLogVideo,
	handleDownloadLogs,
	handleDownloadUptimeLogs,
	handleDownloadAlerts,
	handleDownloadViolationLogs,
	handleDownloadReport,
	getReportJobStatus,
	handleDownloadReports,
	handleDownloadNicApiUsage
};

export default downloadServices;
